import { Box, useMediaQuery, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { setPosts } from "state";
import Navbar from "scenes/navbar";
import UserWidget from "scenes/widgets/UserWidget";
import MyPostWidget from "scenes/widgets/MyPostWidget";
import PostsWidget from "scenes/widgets/PostsWidget";
import AdvertWidget, { img1 as advertImage } from "scenes/widgets/AdvertWidget";
import { preloadImages } from "utils/imageUtils";
import { BASE_URL } from "../../config";

const HomePage = () => {
  const isNonMobileScreens = useMediaQuery("(min-width:1000px)");
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const token = useSelector((state) => state.token);
  const isAuth = Boolean(token);

  // For guest users, user object won't exist
  const { _id, picturePath } = user || {};

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // 1. Fetch Posts
      const postsPromise = fetch(`${BASE_URL}/posts`, {
        method: "GET",
        headers: headers,
      }).then((res) => res.json());

      // 2. Fetch User Data (only if authenticated and user exists)
      let userPromise = Promise.resolve(null);
      if (_id) {
        userPromise = fetch(`${BASE_URL}/users/${_id}`, {
          method: "GET",
          headers: headers,
        }).then((res) => res.json());
      }

      const [postsData, fetchedUser] = await Promise.all([postsPromise, userPromise]);

      if (fetchedUser) setUserData(fetchedUser);
      dispatch(setPosts({ posts: postsData }));

      // 3. Preload Images
      const imagesToLoad = [];

      // Advert Image (if on big screen, or simplified just always load)
      if (advertImage) imagesToLoad.push(advertImage);

      // User Avatar
      if (picturePath) imagesToLoad.push(picturePath);

      // Posts Images & User Avatars in posts
      if (Array.isArray(postsData)) {
        postsData.forEach((post) => {
          if (post.picturePath) imagesToLoad.push(post.picturePath);
          if (post.userPicturePath) imagesToLoad.push(post.userPicturePath);
          if (Array.isArray(post.comments)) {
            post.comments.forEach((comment) => {
              if (comment.userPicturePath) imagesToLoad.push(comment.userPicturePath);
            });
          }
        });
      }

      // Friends' Avatars (from fetched user)
      if (fetchedUser && Array.isArray(fetchedUser.friends)) {
        fetchedUser.friends.forEach((friend) => {
          if (friend.picturePath) imagesToLoad.push(friend.picturePath);
        });
      }

      await preloadImages(imagesToLoad);

    } catch (error) {
      console.error("Failed to load home page data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Navbar />
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="calc(100vh - 6rem)" // Approximate height minus navbar
          bgcolor="background.default"
        >
          <CircularProgress size="4rem" />
        </Box>
      ) : (
        <Box
          width="100%"
          padding="1rem 3%"
          display={isNonMobileScreens ? "flex" : "block"}
          // gap="0.5rem"
          justifyContent="space-between"
        >
          {isNonMobileScreens && (
            <Box flexBasis={isNonMobileScreens ? "26%" : undefined}>
              <UserWidget userId={_id} picturePath={picturePath} isAuth={isAuth} showLoginPrompt={true} userData={userData} />
            </Box>
          )}
          <Box
            flexBasis={isNonMobileScreens ? "42%" : undefined}
          // mt={isNonMobileScreens ? undefined : "2rem"}
          >
            {isAuth && <MyPostWidget picturePath={picturePath} />}
            <PostsWidget userId={_id} shouldFetch={false} />
          </Box>
          {isNonMobileScreens && (
            <Box flexBasis="26%">
              <AdvertWidget />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HomePage;